// The ONE section jump strip of every full-screen flow on phone AND tablet
// (KAN-1078): Log Job, the form frame and the estimate builder all render this.
// Pure: the page decides the section list and owns the active key + jump via
// useSectionJump. The first section is active at open (`activeKey` null/unknown
// falls back to it); the active chip is kept in view. `statusDots` adds the
// estimate's per-section status dot (sections carry `status`).
import { useEffect, useRef } from 'react';
import { layoutStyles as L } from '../../../pages/logJobLayoutStyles';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { foundation } from '../../../ui/tokens';

const { spacePx, color, shadow } = foundation;
const EDGE_PAD = 12;
const FOCUS_CLASS = 'cvx-section-jump-focus';
const FOCUS_CSS = `.${FOCUS_CLASS}:focus-visible { outline: none; box-shadow: ${shadow.focus}; }`;
const DOT = 8;
const dot = { width: DOT, height: DOT, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box' };
const dotPill = { display: 'inline-flex', alignItems: 'center', gap: spacePx[1] };

/** done = success, problem = danger, anything else muted (same tones as the estimate checklist). */
function dotColor(status) {
  if (status === 'done') return color.semantic.success;
  if (status === 'problem') return color.semantic.danger;
  return color.neutral.textMuted;
}

/**
 * Keep the active chip fully inside the strip's own horizontal viewport. Sets the STRIP's scroll
 * position only (never scrollIntoView, which can also scroll the page vertically and fight the
 * section jump). Works for scroll-driven and tap-driven changes alike.
 */
export function revealChip(strip, chip, smooth) {
  if (!strip || !chip) return;
  const left = chip.offsetLeft;
  const right = left + chip.offsetWidth;
  const view = strip.clientWidth;
  let target = strip.scrollLeft;
  if (left - EDGE_PAD < target) target = left - EDGE_PAD;
  else if (right + EDGE_PAD > target + view) target = right + EDGE_PAD - view;
  target = Math.max(0, target);
  if (target === strip.scrollLeft) return;
  if (smooth && typeof strip.scrollTo === 'function') strip.scrollTo({ left: target, behavior: 'smooth' });
  else strip.scrollLeft = target;
}

/**
 * @param {object} props
 * @param {Array<{key: string, label?: string, shortLabel?: string, status?: string}>} props.sections
 * @param {string|null} props.activeKey
 * @param {(key: string) => void} props.onJump
 * @param {boolean} [props.statusDots] - a status dot per pill (estimate).
 * @param {string} [props.ariaLabel] - the <nav>'s accessible name (when given the strip renders as a <nav>).
 * @param {(section: object) => string} [props.pillAriaLabel]
 * @param {string} [props.stripTestId]
 * @param {string} [props.pillTestIdPrefix] - pill id = prefix + section key.
 */
export default function FullScreenSectionJumpStrip({
  sections, activeKey, onJump, statusDots = false, ariaLabel, pillAriaLabel,
  stripTestId = 'logjob-jump-strip', pillTestIdPrefix = 'logjob-jump-',
}) {
  const stripRef = useRef(null);
  const reduced = useReducedMotion();
  const current = activeKey && (sections || []).some((s) => s.key === activeKey) ? activeKey : (sections && sections[0] ? sections[0].key : null);
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip || !current) return;
    revealChip(strip, strip.querySelector(`[data-testid="${pillTestIdPrefix}${current}"]`), !reduced);
  }, [current, reduced, pillTestIdPrefix]);
  if (!sections || sections.length === 0) return null;
  const Tag = ariaLabel ? 'nav' : 'div';
  return (
    <Tag ref={stripRef} style={{ ...L.jumpStrip, position: 'relative' }} aria-label={ariaLabel} data-testid={stripTestId}>
      <style>{FOCUS_CSS}</style>
      {sections.map((s) => {
        const active = s.key === current;
        return (
          <button
            key={s.key}
            type="button"
            className={FOCUS_CLASS}
            style={{
              ...L.jumpPill,
              ...(active ? L.jumpPillActive : {}),
              ...(statusDots ? dotPill : {}),
              // Always the full shorthand: toggling only borderColor left an inactive pill with the browser default.
              border: `1px solid ${active ? color.brand.primary : color.neutral.border}`,
            }}
            onClick={() => onJump(s.key)}
            aria-current={active ? 'true' : undefined}
            aria-label={pillAriaLabel ? pillAriaLabel(s) : undefined}
            data-testid={`${pillTestIdPrefix}${s.key}`}
            data-status={statusDots ? s.status : undefined}
          >
            {statusDots ? (
              <span
                aria-hidden
                style={{ ...dot, background: dotColor(s.status), border: active ? `1px solid ${color.brand.onPrimary}` : 'none' }}
              />
            ) : null}
            {s.shortLabel || s.label}
          </button>
        );
      })}
    </Tag>
  );
}
